import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { PublishMeta, PublishValidation } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

interface Props {
  projectId: string;
  onExport?: () => void;
}

type FormKeys = "publisher" | "isbn" | "language" | "edition" | "copyright" | "keywords" | "categories" | "pubDate"
  | "dedication" | "epigraph" | "acknowledgments" | "aboutAuthor" | "alsoBy" | "preface";

const STATUS_ICON = { pass: "check", warn: "alert", fail: "alert" } as const;
const STATUS_CLS = { pass: "insight-ok", warn: "insight-warn", fail: "insight-warn" } as const;

/**
 * Publishing Workspace — publication metadata + front/back matter, and a
 * deterministic pre-flight validation checklist. Advisory only (no platform
 * compliance claims). Export itself uses the existing Export dialog.
 */
export function PublishPanel({ projectId, onExport }: Props) {
  const toast = useToast();
  const [meta, setMeta] = useState<PublishMeta | null>(null);
  const [validation, setValidation] = useState<PublishValidation | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ meta }, v] = await Promise.all([api.publishMeta(projectId), api.validatePublish(projectId)]);
      setMeta(meta); setValidation(v);
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't load publishing info"); }
    finally { setLoading(false); }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load]);

  const set = (k: FormKeys, v: string) => setMeta((m) => m ? { ...m, [k]: v } : m);
  const persist = async (k: FormKeys) => {
    if (!meta) return;
    try { await api.updatePublishMeta(projectId, { [k]: (meta[k] as string) || null }); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Save failed"); }
  };
  const revalidate = async () => {
    try { setValidation(await api.validatePublish(projectId)); toast.success("Validation refreshed."); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Validation failed"); }
  };

  if (loading || !meta) return <div className="insights-loading" style={{ height: "100%" }}><Icon name="download" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;

  const fld = (k: FormKeys, label: string, area = false, placeholder = "") => (
    <div className="field grow">
      <label className="label">{label}</label>
      {area
        ? <textarea className="textarea" rows={3} value={(meta[k] as string) ?? ""} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} onBlur={() => persist(k)} />
        : <input className="input" value={(meta[k] as string) ?? ""} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} onBlur={() => persist(k)} />}
    </div>
  );

  return (
    <div className="publish">
      <div className="publish-head">
        <div><h2 style={{ fontSize: 18 }}>Publish</h2><p className="faint" style={{ fontSize: 12, margin: "2px 0 0" }}>Publication metadata, front/back matter, and a pre-flight check. Export from the topbar Export button.</p></div>
        <div className="row gap-2">
          <button className="btn btn-sm" onClick={revalidate}><Icon name="check" size={14} /> Re-validate</button>
          {onExport && <button className="btn btn-sm btn-primary" onClick={onExport}><Icon name="file" size={14} /> Export…</button>}
        </div>
      </div>

      <div className="publish-grid">
        <section className="card insight-card">
          <div className="insight-card-head"><Icon name="check" size={16} style={{ color: "var(--accent)" }} /><h3>Pre-flight check</h3>
            {validation && <span className="spacer" />}
            {validation && <span className="faint" style={{ fontSize: 11 }}>{validation.counts.pass} ok · {validation.counts.warn} warn · {validation.counts.fail} fail</span>}
          </div>
          <ul className="publish-checks">
            {validation?.checks.map((c) => (
              <li key={c.id} className="publish-check">
                <Icon name={STATUS_ICON[c.status]} size={14} className={STATUS_CLS[c.status]} />
                <div className="grow"><span className="strong">{c.label}</span><div className="faint" style={{ fontSize: 11.5 }}>{c.detail}</div></div>
              </li>
            ))}
          </ul>
          {validation && validation.presets.length > 0 && (
            <div className="publish-presets">
              <div className="faint" style={{ fontSize: 11, marginBottom: 4 }}>Target presets (advisory — verify against each platform's current spec):</div>
              {validation.presets.map((p) => <div key={p.id} className="publish-preset"><span className="chip">{p.label}</span> <span className="faint" style={{ fontSize: 11.5 }}>{p.note}</span></div>)}
            </div>
          )}
        </section>

        <section className="card insight-card">
          <div className="insight-card-head"><Icon name="book" size={16} style={{ color: "var(--accent)" }} /><h3>Metadata</h3></div>
          <p className="faint" style={{ fontSize: 11, margin: "0 0 8px" }}>Title, author, subtitle, series and cover are set in Project Settings.</p>
          <div className="detail-grid">
            {fld("publisher", "Publisher")}
            {fld("isbn", "ISBN")}
            {fld("language", "Language", false, "en")}
            {fld("edition", "Edition")}
            {fld("pubDate", "Publication date")}
            {fld("categories", "Categories / BISAC")}
          </div>
          {fld("keywords", "Keywords", true, "comma-separated")}
          {fld("copyright", "Copyright page", true)}
        </section>

        <section className="card insight-card">
          <div className="insight-card-head"><Icon name="file" size={16} style={{ color: "var(--accent)" }} /><h3>Front &amp; back matter</h3></div>
          {fld("dedication", "Dedication", true)}
          {fld("epigraph", "Epigraph", true)}
          {fld("preface", "Preface", true)}
          {fld("acknowledgments", "Acknowledgments", true)}
          {fld("aboutAuthor", "About the author", true)}
          {fld("alsoBy", "Also by this author", true)}
        </section>
      </div>
    </div>
  );
}
